import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaBook, FaCheckCircle, FaWhatsapp } from 'react-icons/fa';

const editionConfig = {
  micro: {
    color: '#7B2FBE',
    bg: 'linear-gradient(135deg, #F9F0FF 0%, #EDE7F6 100%)',
    border: 'rgba(123,47,190,0.2)',
    badgeBg: '#7B2FBE',
    glowBg: 'rgba(123,47,190,0.08)',
    icon: '🌈',
    decoIcon: '🎨',
  },
  macro: {
    color: '#1565C0',
    bg: 'linear-gradient(135deg, #EFF6FF 0%, #E8EAF6 100%)',
    border: 'rgba(21,101,192,0.2)',
    badgeBg: '#1565C0',
    glowBg: 'rgba(21,101,192,0.08)',
    icon: '🚀',
    decoIcon: '🧩',
  },
};

const EditionCard = ({ edition, onReadSample }) => {
  const cfg = editionConfig[edition.id] || editionConfig.micro;

  return (
    <motion.div
      whileHover={{ y: -10, scale: 1.01 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      className="relative rounded-2xl overflow-hidden h-full"
      style={{
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        boxShadow: `0 8px 40px ${cfg.glowBg}`,
      }}
    >
      {/* Age Badge */}
      <div
        className="absolute top-5 right-5 px-3 py-1.5 rounded-full font-display font-bold text-xs text-white z-10"
        style={{ background: cfg.badgeBg }}
      >
        Ages {edition.ageRange}
      </div>

      {/* Decorative bg icon */}
      <div className="absolute bottom-4 right-4 text-8xl md:text-9xl opacity-[0.07] pointer-events-none select-none">
        {cfg.decoIcon}
      </div>

      <div className="p-7 md:p-9 relative z-10">
        {/* Main Icon — animated */}
        <motion.div
          animate={{ scale: [1, 1.08, 1], rotate: [0, 4, -4, 0] }}
          transition={{ duration: 3.5, repeat: Infinity, repeatDelay: 2 }}
          className="text-7xl text-center mb-5"
        >
          {cfg.icon}
        </motion.div>

        {/* Title */}
        <h2
          className="text-3xl md:text-4xl font-display font-bold text-center mb-3"
          style={{ color: cfg.color }}
        >
          {edition.name}
        </h2>

        {/* Subtitle pill */}
        <div className="flex justify-center mb-5">
          <span
            className="inline-block px-4 py-1.5 rounded-full font-display font-semibold text-sm"
            style={{ background: `${cfg.color}15`, color: cfg.color }}
          >
            Perfect for {edition.ageRange} year olds
          </span>
        </div>

        {/* Description */}
        <p className="text-base text-neutral-gray-dark text-center mb-6 leading-relaxed">
          {edition.description}
        </p>

        {/* Feature List */}
        <div className="mb-7">
          <h3
            className="font-display font-bold text-sm uppercase tracking-wide mb-3 text-center"
            style={{ color: cfg.color, opacity: 0.75 }}
          >
            What's Inside Every Week
          </h3>
          <ul className="space-y-2.5">
            {edition.features.map((feature, index) => (
              <motion.li
                key={index}
                initial={{ opacity: 0, x: -15 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.07 }}
                className="flex items-start gap-3 text-sm text-neutral-gray-dark"
              >
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-white text-xs font-bold"
                  style={{ background: cfg.color }}
                >
                  ✓
                </div>
                {feature}
              </motion.li>
            ))}
          </ul>
        </div>

        {/* Additional Benefits */}
        {edition.additionalBenefits && (
          <div className="mb-6 rounded-xl p-4" style={{ background: `${cfg.color}08`, border: `1px solid ${cfg.color}18` }}>
            <h4 className="font-display font-bold text-sm flex items-center gap-2 mb-2" style={{ color: cfg.color }}>
              <span className="text-xl">✨</span> Bonus Features
            </h4>
            <ul className="space-y-1 text-xs text-neutral-gray-dark">
              {edition.additionalBenefits.map((benefit, i) => (
                <li key={i} className="flex items-center gap-1.5">
                  <span style={{ color: cfg.color }}>•</span> {benefit}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Sample Preview */}
        {edition.samplePages && (
          <div className="mb-6">
            <h4 className="font-display font-semibold text-sm text-center mb-3" style={{ color: cfg.color }}>
              Sample Pages Preview
            </h4>
            <div className="grid grid-cols-3 gap-2">
              {edition.samplePages.map((page, index) => (
                <div
                  key={index}
                  className="aspect-square rounded-xl flex items-center justify-center text-4xl"
                  style={{ background: `${cfg.color}08`, border: `1px solid ${cfg.color}15` }}
                >
                  {page.icon}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-auto">
          <motion.button
            onClick={onReadSample}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            className="flex items-center justify-center gap-2 py-3 px-5 rounded-button font-display font-semibold text-sm text-white transition-all"
            style={{ background: cfg.color, boxShadow: `0 4px 16px ${cfg.color}30` }}
          >
            <FaBook className="text-sm" />
            Read Sample
          </motion.button>

          <Link
            to="/subscribe"
            className="flex items-center justify-center gap-2 py-3 px-5 rounded-button font-display font-semibold text-sm text-white transition-all"
            style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)', boxShadow: '0 4px 16px rgba(37,211,102,0.25)' }}
          >
            <FaWhatsapp className="text-sm" />
            Subscribe
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

export default EditionCard;